import React, { useCallback } from 'react';
import cls from 'classnames';
import type { SharedButtonProps } from '~/components/views/common/buttons/types';
import useMouseDown from '~/hooks/use-mouse-down';
import UIKitButton from '~/components/views/ui-kit/Button/Button';
import GoogleAnalyticsConstants from '~/constants/google-analytics-constants';
import formatMessageWrapper from '~/utils/format-message-wrapper/format-message-wrapper';
import './BaseButton.pcss';

const getTrackingProps = (
  type: SharedButtonProps['type'],
  trackingAction = GoogleAnalyticsConstants.actions.CLICK,
  trackingLabel?: string
) => {
  return {
    'data-trackingcategory':
      type === 'submit'
        ? GoogleAnalyticsConstants.categories.SUBMIT_BUTTON
        : GoogleAnalyticsConstants.categories.BUTTON,
    'data-trackingaction': trackingAction,
    'data-trackinglabel': trackingLabel,
  };
};

const BaseButton: React.FC<SharedButtonProps> = ({
  isSubmitted,
  iconLeft,
  expand,
  langKey,
  className,
  children,
  trackingAction,
  trackingLabel,
  mouseHoldTimer = 150,
  onMouseDown,
  onMouseUp,
  onMouseOut,
  onMouseHold,
  ...otherProps
}) => {
  const [startMouseDown, stopMouseDown] = useMouseDown(
    onMouseHold,
    mouseHoldTimer
  );
  const fixedIconLeft = isSubmitted ? 'success' : iconLeft;

  const classNames = cls(className, {
    'w-full': expand === 'full',
    'w-1/2': expand === 'half',
  });

  let translatedLangKey;
  if (langKey) {
    translatedLangKey = formatMessageWrapper(langKey);
  }

  const trackingProps = getTrackingProps(
    otherProps.type,
    trackingAction,
    trackingLabel
  );

  const handleOnMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onMouseHold && e.button === 0) {
        startMouseDown();
      }
      onMouseDown?.(e);
    },
    [onMouseHold, onMouseDown, startMouseDown]
  );

  const handleOnMouseUp = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onMouseHold && e.button === 0) {
        stopMouseDown();
      }
      onMouseUp?.(e);
    },
    [onMouseHold, onMouseUp, stopMouseDown]
  );

  const handleOnMouseOut = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onMouseHold) {
        stopMouseDown();
      }
      onMouseOut?.(e);
    },
    [onMouseHold, onMouseOut, stopMouseDown]
  );

  return (
    <UIKitButton
      {...trackingProps}
      {...otherProps}
      onMouseDown={handleOnMouseDown}
      onMouseUp={handleOnMouseUp}
      onMouseOut={handleOnMouseOut}
      className={classNames}
      iconLeft={fixedIconLeft}
    >
      {translatedLangKey}
      {children}
    </UIKitButton>
  );
};

export default BaseButton;
